import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import {
  IPost,
  ILikesResponse,
  ICommentResponse,
  ICommentResponseBody,
  IPostDeleteResponseBody,
  PostState,
  IVisibilityResponse,
  IPostEditResponse,
  IPostCreateResponse,
  IPostListResponse,
  ICommentsResponse,
  IPagination,
  IRecentComment,
  ICommentPaginationState,
} from '../../interface/postModel';
import {
  createPost,
  deleteCommentById,
  deletePostThunk,
  editPost,
  fetchCommentById,
  fetchLikeById,
  fetchVisibility,
  postComment,
  toggleLike,
  fetchPostPaginated,
  fetchMorePosts,
  fetchPostCommentsPaginated,
} from '../action/post.action';

// Extended state interface for pagination
interface ExtendedPostState extends PostState {
  // Paginated posts state
  paginatedPosts: IPost[];
  pagination: IPagination | null;
  isLoadingPaginated: boolean;
  isLoadingMore: boolean;
  paginatedError: string | null;
  // Comment pagination per post
  commentPagination: Record<string, ICommentPaginationState>;
}

const initialState: ExtendedPostState = {
  posts: null,
  likes: null,
  comments: null,
  isLoading: false,
  visibility: null,
  error: null,
  // Pagination initial state
  paginatedPosts: [],
  pagination: null,
  isLoadingPaginated: false,
  isLoadingMore: false,
  paginatedError: null,
  commentPagination: {},
};

const postSlice = createSlice({
  name: 'post',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    // Clear paginated posts (useful for refresh)
    clearPaginatedPosts(state) {
      state.paginatedPosts = [];
      state.pagination = null;
      state.paginatedError = null;
    },
    // Clear comment pagination for a specific post
    clearCommentPagination(state, action: PayloadAction<string>) {
      delete state.commentPagination[action.payload];
    },
    // Update like count optimistically for paginated posts
    updatePaginatedPostLike(
      state,
      action: PayloadAction<{ postId: string; isLiked: boolean; likesCount: number }>,
    ) {
      const { postId, isLiked, likesCount } = action.payload;
      const postIndex = state.paginatedPosts.findIndex((p) => p.id === postId);
      if (postIndex !== -1) {
        state.paginatedPosts[postIndex].isLiked = isLiked;
        state.paginatedPosts[postIndex].likesCount = likesCount;
      }
    },
    // Remove a post optimistically
    removePaginatedPost(state, action: PayloadAction<string>) {
      state.paginatedPosts = state.paginatedPosts.filter((p) => p.id !== action.payload);
      if (state.pagination) {
        state.pagination.total -= 1;
      }
    },
    // Restore a post that was optimistically removed (on delete failure)
    restorePaginatedPost(state, action: PayloadAction<{ post: IPost; index: number }>) {
      const { post, index } = action.payload;
      state.paginatedPosts.splice(index, 0, post);
      if (state.pagination) {
        state.pagination.total += 1;
      }
    },
    // Update comment count optimistically
    updatePaginatedPostCommentCount(
      state,
      action: PayloadAction<{ postId: string; commentCount: number }>,
    ) {
      const { postId, commentCount } = action.payload;
      const postIndex = state.paginatedPosts.findIndex((p) => p.id === postId);
      if (postIndex !== -1) {
        state.paginatedPosts[postIndex].commentsCount = commentCount;
      }
    },
    // Add a new comment to recent comments optimistically
    addPaginatedPostComment(
      state,
      action: PayloadAction<{
        postId: string;
        comment: IRecentComment;
      }>,
    ) {
      const { postId, comment } = action.payload;
      const postIndex = state.paginatedPosts.findIndex((p) => p.id === postId);
      if (postIndex !== -1) {
        // Add comment to the beginning of recent comments
        const currentComments = state.paginatedPosts[postIndex].recentComments || [];
        state.paginatedPosts[postIndex].recentComments = [comment, ...currentComments].slice(0, 2); // Keep only 2 recent comments
        // Increment comment count
        state.paginatedPosts[postIndex].commentsCount += 1;
      }

      // Also update paginated comments if they've been fetched (user clicked "View all")
      if (state.commentPagination && state.commentPagination[postId]) {
        // Add to beginning of paginated comments list
        state.commentPagination[postId].comments = [
          comment,
          ...state.commentPagination[postId].comments,
        ];
        // Update pagination total count
        if (state.commentPagination[postId].pagination) {
          state.commentPagination[postId].pagination!.total += 1;
        }
      }
    },
    // Remove a comment from recent and paginated comments optimistically
    removePaginatedPostComment(
      state,
      action: PayloadAction<{ postId: string; commentId: string }>,
    ) {
      const { postId, commentId } = action.payload;
      const postIndex = state.paginatedPosts.findIndex((p) => p.id === postId);
      if (postIndex !== -1) {
        const currentComments = state.paginatedPosts[postIndex].recentComments || [];
        state.paginatedPosts[postIndex].recentComments = currentComments.filter(
          (c) => c.id !== commentId,
        );
        if (state.paginatedPosts[postIndex].commentsCount > 0) {
          state.paginatedPosts[postIndex].commentsCount -= 1;
        }

        // Backfill recentComments from paginated cache if available
        // This prevents showing "no comments" when more comments exist on server
        const updatedRecent = state.paginatedPosts[postIndex].recentComments;
        const remainingCount = state.paginatedPosts[postIndex].commentsCount;
        if (updatedRecent.length < 2 && remainingCount > updatedRecent.length) {
          const cachedComments = state.commentPagination?.[postId]?.comments || [];
          if (cachedComments.length > 0) {
            // Get IDs already in recentComments to avoid duplicates
            const existingIds = new Set(updatedRecent.map((c) => c.id));
            const backfillCandidates = cachedComments.filter(
              (c) => !existingIds.has(c.id) && c.id !== commentId,
            );
            const needed = 2 - updatedRecent.length;
            const backfill = backfillCandidates.slice(0, needed);
            state.paginatedPosts[postIndex].recentComments = [...updatedRecent, ...backfill];
          }
        }
      }

      // Also update paginated comments if they've been fetched
      if (state.commentPagination && state.commentPagination[postId]) {
        state.commentPagination[postId].comments = state.commentPagination[postId].comments.filter(
          (c) => c.id !== commentId,
        );
        if (
          state.commentPagination[postId].pagination &&
          state.commentPagination[postId].pagination!.total > 0
        ) {
          state.commentPagination[postId].pagination!.total -= 1;
        }
      }
    },
    // Update recentComments for a post from freshly fetched paginated data
    updateRecentCommentsFromPaginated(
      state,
      action: PayloadAction<{ postId: string; comments: IRecentComment[]; total: number }>,
    ) {
      const { postId, comments, total } = action.payload;
      const postIndex = state.paginatedPosts.findIndex((p) => p.id === postId);
      if (postIndex !== -1) {
        // Take up to 2 latest comments for the recent view
        state.paginatedPosts[postIndex].recentComments = comments.slice(0, 2);
        state.paginatedPosts[postIndex].commentsCount = total;
      }
    },
  },
  extraReducers: (builder) => {
    // Toggle Like
    builder.addCase(toggleLike.pending, (state) => {
      state.error = null;
    });
    builder.addCase(toggleLike.fulfilled, (_state) => {
      // you may re-fetch likes here or just assume success
    });
    builder.addCase(toggleLike.rejected, (state, action) => {
      state.error =
        action.payload?.message || i18n.t('toast.failed_to_toggle_like', 'Failed to toggle like');
    });

    builder.addCase(fetchLikeById.pending, (state) => {
      state.error = null;
    });
    builder.addCase(fetchLikeById.fulfilled, (state, action: PayloadAction<ILikesResponse>) => {
      state.likes = action.payload.data;
    });
    builder.addCase(fetchLikeById.rejected, (state, action) => {
      state.error =
        action.payload?.message || i18n.t('toast.failed_to_fetch_likes', 'Failed to fetch likes');
    });

    // Post Comment
    builder.addCase(postComment.pending, (state) => {
      state.error = null;
    });
    builder.addCase(
      postComment.fulfilled,
      (state, _action: PayloadAction<ICommentResponseBody>) => {
        state.isLoading = false;
      },
    );
    builder.addCase(postComment.rejected, (state, action) => {
      state.error =
        action.payload?.message || i18n.t('toast.failed_to_post_comment', 'Failed to post comment');
    });

    // // Fetch Comments
    builder.addCase(fetchCommentById.pending, (state) => {
      state.error = null;
    });
    builder.addCase(
      fetchCommentById.fulfilled,
      (state, action: PayloadAction<ICommentResponse>) => {
        state.comments = action.payload.data;
      },
    );
    builder.addCase(fetchCommentById.rejected, (state, action) => {
      state.error =
        action.payload?.message ||
        i18n.t('toast.failed_to_fetch_comments', 'Failed to fetch comments');
    });

    // // Delete Comment
    builder.addCase(deleteCommentById.pending, (state) => {
      state.error = null;
    });
    builder.addCase(
      deleteCommentById.fulfilled,
      (state, _action: PayloadAction<IPostDeleteResponseBody>) => {
        state.isLoading = false;
      },
    );
    builder.addCase(deleteCommentById.rejected, (state, action) => {
      state.error =
        action.payload?.message ||
        i18n.t('toast.failed_to_delete_comment', 'Failed to delete comment');
    });

    // Fetch Visibility
    builder.addCase(fetchVisibility.pending, (state) => {
      state.visibility = null;
      state.error = null;
    });
    builder.addCase(
      fetchVisibility.fulfilled,
      (state, action: PayloadAction<IVisibilityResponse>) => {
        state.visibility = action.payload.data.response;
        state.error = null;
      },
    );
    builder.addCase(fetchVisibility.rejected, (state, action) => {
      state.visibility = null;
      state.error =
        action.payload?.message ||
        i18n.t('toast.failed_to_fetch_visibility', 'Failed to fetch visibility');
    });
    builder.addCase(createPost.pending, (state, _action) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createPost.fulfilled, (state, action: PayloadAction<IPostCreateResponse>) => {
      state.isLoading = false;
      state.error = null;

      // Prepend the new post to the posts array
      if (action.payload?.data?.post) {
        state.paginatedPosts = [action.payload.data.post, ...state.paginatedPosts];

        // Update pagination total count
        if (state.pagination) {
          state.pagination.total += 1;
        }
      }
    });
    builder.addCase(createPost.rejected, (state, action) => {
      state.isLoading = false;
      state.error =
        action.payload?.message || i18n.t('toast.failed_to_create_post', 'Failed to create post');
    });

    // Edit Post
    builder.addCase(editPost.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(editPost.fulfilled, (state, action: PayloadAction<IPostEditResponse>) => {
      state.isLoading = false;
      state.error = null;

      // Find and update the edited post in the array
      if (action.payload?.data?.post) {
        const updatedPost = action.payload.data.post;
        const postIndex = state.paginatedPosts.findIndex((p) => p.id === updatedPost.id);

        if (postIndex !== -1) {
          state.paginatedPosts[postIndex] = updatedPost;
        }
      }
    });
    builder.addCase(editPost.rejected, (state, action) => {
      state.isLoading = false;
      state.error =
        action.payload?.message || i18n.t('toast.failed_to_edit_post', 'Failed to edit post');
    });

    // ==================== Pagination Extra Reducers ====================

    // Fetch Paginated Posts (initial load / refresh)
    builder.addCase(fetchPostPaginated.pending, (state) => {
      state.isLoadingPaginated = true;
      state.paginatedError = null;
    });
    builder.addCase(
      fetchPostPaginated.fulfilled,
      (state, action: PayloadAction<IPostListResponse>) => {
        state.isLoadingPaginated = false;
        state.paginatedPosts = action.payload.data.post;
        state.pagination = action.payload.data.pagination;
        state.paginatedError = null;
      },
    );
    builder.addCase(fetchPostPaginated.rejected, (state, action) => {
      state.isLoadingPaginated = false;
      state.paginatedError =
        action.payload?.message || i18n.t('toast.failed_to_fetch_posts', 'Failed to fetch posts');
    });

    // Fetch More Posts (infinite scroll)
    builder.addCase(fetchMorePosts.pending, (state) => {
      state.isLoadingMore = true;
    });
    builder.addCase(fetchMorePosts.fulfilled, (state, action: PayloadAction<IPostListResponse>) => {
      state.isLoadingMore = false;
      // Append new posts to existing
      state.paginatedPosts = [...state.paginatedPosts, ...action.payload.data.post];
      state.pagination = action.payload.data.pagination;
    });
    builder.addCase(fetchMorePosts.rejected, (state, action) => {
      state.isLoadingMore = false;
      state.paginatedError =
        action.payload?.message ||
        i18n.t('toast.failed_to_fetch_more_posts', 'Failed to fetch more posts');
    });

    // Fetch Paginated Comments
    builder.addCase(fetchPostCommentsPaginated.pending, (state, action) => {
      const postId = action.meta.arg.postId;

      // Ensure commentPagination is initialized as an object
      if (!state.commentPagination) {
        state.commentPagination = {};
      }

      // Ensure this post's comment state is initialized
      if (!state.commentPagination[postId]) {
        state.commentPagination[postId] = {
          comments: [],
          pagination: null,
          isLoading: false,
          isLoadingMore: false,
        };
      }

      // Determine if this is initial load or load more
      const isFirstPage = action.meta.arg.page === 1;
      if (isFirstPage) {
        state.commentPagination[postId].isLoading = true;
      } else {
        state.commentPagination[postId].isLoadingMore = true;
      }
    });
    builder.addCase(
      fetchPostCommentsPaginated.fulfilled,
      (state, action: PayloadAction<ICommentsResponse & { postId: string }>) => {
        const { postId } = action.payload;
        const { data } = action.payload;

        // Ensure commentPagination is initialized as an object
        if (!state.commentPagination) {
          state.commentPagination = {};
        }

        // Ensure this post's comment state is initialized
        if (!state.commentPagination[postId]) {
          state.commentPagination[postId] = {
            comments: [],
            pagination: null,
            isLoading: false,
            isLoadingMore: false,
          };
        }

        const isFirstPage = data.pagination.page === 1;

        if (isFirstPage) {
          // Replace comments for first page
          state.commentPagination[postId].comments = data.comments;
          state.commentPagination[postId].pagination = data.pagination;
          state.commentPagination[postId].isLoading = false;
          state.commentPagination[postId].isLoadingMore = false;
        } else {
          // Append comments for subsequent pages
          state.commentPagination[postId].comments = [
            ...state.commentPagination[postId].comments,
            ...data.comments,
          ];
          state.commentPagination[postId].pagination = data.pagination;
          state.commentPagination[postId].isLoading = false;
          state.commentPagination[postId].isLoadingMore = false;
        }
      },
    );
    builder.addCase(fetchPostCommentsPaginated.rejected, (state, action) => {
      const postId = action.meta.arg.postId;

      // Ensure commentPagination is initialized as an object
      if (!state.commentPagination) {
        state.commentPagination = {};
      }

      // Ensure this post's comment state is initialized
      if (!state.commentPagination[postId]) {
        state.commentPagination[postId] = {
          comments: [],
          pagination: null,
          isLoading: false,
          isLoadingMore: false,
        };
      }

      state.commentPagination[postId].isLoading = false;
      state.commentPagination[postId].isLoadingMore = false;
    });

    // Delete Post
    builder.addCase(deletePostThunk.pending, (state) => {
      state.error = null;
    });
    builder.addCase(deletePostThunk.fulfilled, (state) => {
      state.error = null;
    });
    builder.addCase(deletePostThunk.rejected, (state, action) => {
      state.error =
        action.payload?.message || i18n.t('toast.failed_to_delete_post', 'Failed to delete post');
    });
  },
});

export const {
  clearError,
  clearPaginatedPosts,
  clearCommentPagination,
  updatePaginatedPostLike,
  updatePaginatedPostCommentCount,
  addPaginatedPostComment,
  removePaginatedPostComment,
  updateRecentCommentsFromPaginated,
  removePaginatedPost,
  restorePaginatedPost,
} = postSlice.actions;
export default postSlice.reducer;
